/**
 * Phase 7.4 — keyboard-shortcut help overlay.
 *
 * Press `?` from anywhere outside a text input to toggle this dialog.
 * Lists every globally-registered shortcut so users can discover
 * navigation/search/sidepanel toggles without reading docs.
 *
 * The shortcut metadata below is the single source of truth — both
 * the App.tsx keydown handler and this dialog read from it. Adding a
 * new global shortcut means one entry here plus the matching dispatch
 * in App.tsx.
 */

import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Keyboard } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useShortcutHelpStore } from "@/stores/useShortcutHelpStore";

export interface ShortcutSpec {
  /** Keys shown in the dialog. Use Mac convention (`⌘`); we don't
   *  swap to "Ctrl" on Windows because the keydown handler matches
   *  both `metaKey` and `ctrlKey` for the same shortcut. */
  keys: readonly string[];
  /** i18n key under `shortcuts.items.*` — resolved at render time so
   *  the description swaps with the active locale. The const list
   *  carries the keys, not the literal strings, so adding a new
   *  shortcut means one entry here + matching translations in all
   *  three locale JSON files. */
  descriptionKey: string;
}

export const GLOBAL_SHORTCUTS: readonly ShortcutSpec[] = [
  { keys: ["⌘ / Ctrl", "K"], descriptionKey: "shortcuts.items.commandPalette" },
  { keys: ["⌘ / Ctrl", "B"], descriptionKey: "shortcuts.items.toggleSidePanel" },
  { keys: ["Enter"], descriptionKey: "shortcuts.items.sendMessage" },
  { keys: ["⌘ / Ctrl", "Enter"], descriptionKey: "shortcuts.items.sendMessageAlias" },
  { keys: ["Shift", "Enter"], descriptionKey: "shortcuts.items.newline" },
  { keys: ["?"], descriptionKey: "shortcuts.items.showHelp" },
  { keys: ["⌘ / Ctrl", "/"], descriptionKey: "shortcuts.items.showHelpAlias" },
  { keys: ["Esc"], descriptionKey: "shortcuts.items.closeOverlay" },
];

export function ShortcutHelpDialog() {
  const { t } = useTranslation();
  const open = useShortcutHelpStore((s) => s.open);
  const setOpen = useShortcutHelpStore((s) => s.setOpen);

  // Esc-to-close is handled by Radix's `Dialog.Root` via the
  // `onOpenChange` prop below — no manual keydown handler needed.

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm dark:bg-slate-950/60" />
        <Dialog.Content
          data-testid="shortcut-help-dialog"
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
        >
          <VisuallyHidden>
            <Dialog.Description>{t("shortcuts.dialogDescription")}</Dialog.Description>
          </VisuallyHidden>

          <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <Keyboard size={16} className="text-slate-500 dark:text-slate-400" />
            <Dialog.Title className="text-sm font-medium text-slate-700 dark:text-slate-200">
              {t("shortcuts.title")}
            </Dialog.Title>
          </div>

          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {GLOBAL_SHORTCUTS.map((s) => (
              <li
                key={s.keys.join("+")}
                className="flex items-center justify-between gap-4 px-4 py-3 text-sm"
              >
                <span className="text-slate-600 dark:text-slate-300">{t(s.descriptionKey)}</span>
                <span className="flex shrink-0 items-center gap-1">
                  {s.keys.map((k, idx) => (
                    <span key={idx} className="flex items-center gap-1">
                      {idx > 0 && (
                        <span className="text-xs text-slate-400 dark:text-slate-500">+</span>
                      )}
                      <kbd className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-xs text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        {k}
                      </kbd>
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
